import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router';
import { CookieConsent } from '../components/CookieConsent';
import { Calendar, List, Users, User, LogOut, CalendarCheck, Shield, ChevronDown, HandHelping, Award } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../lib/auth';
import logoImage from '@/assets/aa3d17232330c68b70c722c53cf397bdb46eec0a.png';
import cercetasiiLogo from '@/assets/cercetasii-logo.png';

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isStaffOrPartner = user?.is_staff || (user?.partner_ids && user.partner_ids.length > 0);

  if (!user) {
    return (
      <Link
        to="/cont"
        className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
        style={{ fontFamily: 'var(--font-primary)' }}
      >
        <User className="h-4 w-4" />
        Conectare
      </Link>
    );
  }

  const initials = user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : user.email.substring(0, 2).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
        style={{ fontFamily: 'var(--font-primary)' }}
      >
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: 'var(--vivid-purple)' }}
        >
          {initials}
        </div>
        <span className="hidden lg:inline">{user.first_name || user.email}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-56 rounded-xl border bg-white py-2 shadow-lg">
          <div className="border-b px-4 py-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                {user.first_name ? `${user.first_name} ${user.last_name}` : user.email}
              </p>
              {user.profile?.is_volunteer && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                  <HandHelping className="h-3 w-3" />
                  Voluntar
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>

          <Link
            to="/cont"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <User className="h-4 w-4 text-muted-foreground" />
            Contul meu
          </Link>
          <Link
            to="/cont/evenimente"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <CalendarCheck className="h-4 w-4 text-muted-foreground" />
            Evenimentele mele
          </Link>

          {isStaffOrPartner && (
            <>
              <div className="my-1 border-t" />
              <Link
                to="/staff"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ fontFamily: 'var(--font-secondary)', color: 'var(--vivid-purple)' }}
              >
                <Shield className="h-4 w-4" />
                Zona Staff
              </Link>
            </>
          )}

          <div className="my-1 border-t" />
          <button
            onClick={() => { setOpen(false); logout(); }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <LogOut className="h-4 w-4" />
            Deconectare
          </button>
        </div>
      )}
    </div>
  );
}

export default function Root() {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'var(--font-primary)' }}>
      {/* Desktop Header - Hidden on Mobile */}
      <header className="sticky top-0 z-50 border-b bg-white shadow-sm hidden md:block">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoImage} className="h-10 w-auto" alt="Logo" />
            </Link>

            <nav className="flex items-center gap-8">
              <Link
                to="/evenimente"
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === '/' || location.pathname.startsWith('/evenimente') || location.pathname.startsWith('/eveniment')
                    ? 'border-[#3b0b86] text-[#3b0b86]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <List className="h-4 w-4" />
                Evenimente
              </Link>
              <Link
                to="/calendar"
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/calendar') || location.pathname.startsWith('/zi')
                    ? 'border-[#3b0b86] text-[#3b0b86]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <Calendar className="h-4 w-4" />
                Calendar
              </Link>
              <Link
                to="/parteneri"
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  (location.pathname === '/parteneri' || location.pathname.startsWith('/parteneri/') || location.pathname.startsWith('/partener/'))
                    ? 'border-[#3b0b86] text-[#3b0b86]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <Users className="h-4 w-4" />
                Prieteni
              </Link>
              <Link
                to="/parteneri-sponsori"
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/parteneri-sponsori')
                    ? 'border-[#3b0b86] text-[#3b0b86]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <Award className="h-4 w-4" />
                Parteneri & Sponsori
              </Link>
              <UserMenu />
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Header - Logo only, no hamburger */}
      <header className="sticky top-0 z-50 border-b bg-white shadow-sm md:hidden">
        <div className="flex h-14 items-center justify-center px-4">
          <Link to="/">
            <img src={logoImage} className="h-8 w-auto" alt="Logo" />
          </Link>
        </div>
      </header>

      {/* Main Content — bottom padding on mobile for the fixed nav bar */}
      <main className="pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <BottomNav />

      {/* Footer - Desktop only (mobile has bottom nav) */}
      <footer className="hidden border-t bg-white py-8 mt-16 md:block">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            #yoUthNITED · #WECREATE · Alba Iulia, Capitala Tineretului din România 2026
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground/60" style={{ fontFamily: 'var(--font-secondary)' }}>
            <Link to="/confidentialitate" className="underline hover:text-muted-foreground">Confidențialitate</Link>
            <span>·</span>
            <Link to="/termeni" className="underline hover:text-muted-foreground">Termeni</Link>
            <span>·</span>
            <Link to="/cookies" className="underline hover:text-muted-foreground">Cookies</Link>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground/60 flex items-center justify-center gap-1" style={{ fontFamily: 'var(--font-secondary)' }}>
            Powered with ❤️ by{' '}
            <a href="https://yetilabs.ro" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">
              yetilabs.ro
            </a>
            {' '}|{' '}găzduit de{' '}
            <a href="https://albascout.ro" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-muted-foreground">
              <img src={cercetasiiLogo} alt="Cercetașii României Alba Iulia" className="inline-block h-5 w-5" />
              Cercetașii României Alba Iulia
            </a>
          </p>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}
